import { Component, DestroyRef, ElementRef, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/core';
import {
  AssessmentAlternative,
  AssessmentAnnulTypeEnum,
  AssessmentApplicationMode,
  AssessmentQuestionType,
  AssessmentWeightType,
} from '@app/core/models';
import { SCORE_CONFIG } from '@app/modules/eclass/components/modal-daily/base';
import { notNull, toLetter } from '@app/shared';
import { Popover, Strategy } from '@app/shared/modules/template/components/popover';
import { REDUCED } from '@app/shared/modules/text-editor/config';
import { Store } from '@ngrx/store';
import { clone, first, isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, take, tap } from 'rxjs';
import { PopoverAnnulQuestionComponent } from '../../../modal-evaluation-report/components/popover-annul-question/popover-annul-question.component';
import { ModalAuditActions, ModalAuditSelectors } from '../../store';
import {
  Application,
  AttemptQuestionData,
  AttemptStudentData,
  ModalAuditState,
  QuestionData,
} from '../../store/modal-audit.state';
import { onCalculateScaleScore, SCALES } from '../../utils';

enum AnswerResultEnum {
  SelectedAndRight,
  SelectedAndWrong,
  Right,
  None,
}

@Component({
  selector: 'app-card-question-active',
  templateUrl: './card-question-active.component.html',
})
export class CardQuestionActiveComponent implements OnDestroy {
  public readonly editorConfigToolbar = REDUCED;
  public readonly assessmentQuestionType = AssessmentQuestionType;
  public readonly scoreConfig = clone(SCORE_CONFIG);
  public readonly SCALES = SCALES;
  public question$ = new BehaviorSubject<QuestionData>(undefined);
  public answer$: Observable<AttemptQuestionData>;
  public form: FormGroup;
  private _student$ = new BehaviorSubject<AttemptStudentData>(undefined);
  private _application: Application;

  constructor(
    private _store: Store<ModalAuditState>,
    private _destroyRef: DestroyRef,
    private _formBuilder: FormBuilder,
    private _popover: Popover,
    private _alertService: AlertService,
  ) {
    this._store
      .select(ModalAuditSelectors.selectApplication)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull(), distinctUntilChanged(isEqual))
      .subscribe((application) => (this._application = application));

    this._store
      .select(ModalAuditSelectors.selectStudentActive)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull())
      .subscribe((student) => this._student$.next(student));

    this.answer$ = combineLatest({
      question: this.question$,
      student: this._student$,
    }).pipe(
      tap(this.initForm),
      map(
        ({ student, question }): AttemptQuestionData =>
          first(
            student.tentativa_questoes?.filter(
              (tentativaQuestao: any) => tentativaQuestao.questao.uuid === question.uuid,
            ),
          ),
      ),
      tap((answer) =>
        this.form.patchValue({
          score: answer?.nota,
          feedback: answer?.feedback,
        }),
      ),
    );

    this._store
      .select(ModalAuditSelectors.selectQuestionActive)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull())
      .subscribe((question) => this.question$.next(question));
  }

  public get isObjective(): boolean {
    return this.question$.value.tipo === AssessmentQuestionType.Objetiva;
  }

  public get isPercent(): boolean {
    return this._application.assessment.weight_type === AssessmentWeightType.Porcentagem;
  }

  public get hasResponseCard(): boolean {
    return !!this._student$.value?.tentativa_uuid && !!this._student$.value?.tentativa_response_card_upload?.id;
  }

  public get isOnline(): boolean {
    return this._application.mode === AssessmentApplicationMode.Online;
  }

  public ngOnDestroy(): void {
    this.question$.complete();
    this._student$.complete();
  }

  public onGetLetter(index: number): string {
    return toLetter(index);
  }

  public onGetClass(answer: AttemptQuestionData, alternative: AssessmentAlternative): object {
    const options = {
      [AnswerResultEnum.SelectedAndRight]: {
        button: 'text-white !shadow-inner !shadow-black/40 !bg-base-mantis-500',
        icon: '!text-white',
        span: '!border-base-mantis-500 text-base-mantis-500',
      },
      [AnswerResultEnum.SelectedAndWrong]: {
        button: 'text-white !shadow-inner !shadow-black/40 !bg-base-status-danger',
        icon: '!text-white',
        span: '!border-base-status-danger text-base-status-danger',
      },
      [AnswerResultEnum.Right]: {
        button: '',
        icon: '',
        span: '!border-base-mantis-500 text-base-mantis-500',
      },
      [AnswerResultEnum.None]: {
        button: '',
        icon: '',
        span: '',
      },
    };

    const status = this.answerStatus(answer, alternative);

    return options[status];
  }

  public onCalculateScaleScore(weight: number, scale: number): { score: number } {
    return onCalculateScaleScore(weight, scale);
  }

  public onSetScore(score: number) {
    this._alertService
      .confirm({
        title: 'geral.atencao',
        message: 'geral.confirmacao',
      })
      .pipe(take(1), notNull())
      .subscribe(() => {
        this.form.controls.score.setValue(score);
        this.save();
      });
  }

  public onOpenAnullQuestion(elementRef: ElementRef, question: QuestionData) {
    const data = {
      question,
      aplicacao_uuid: this._application.uuid,
      tentativa_uuid: this._student$.value.tentativa_uuid,
      type: AssessmentAnnulTypeEnum.Answer,
    };
    this._popover
      .open(PopoverAnnulQuestionComponent, {
        positionStrategy: [Strategy.LEFT],
        elementRef,
        data,
      })
      .closed.subscribe((response) =>
        this._store.dispatch(
          ModalAuditActions.UpdateAttemptQuestionReducer({
            response,
            data: {
              ...data,
              question_uuid: question.uuid,
              application_uuid: this._application.uuid,
              attempt_uuid: this._student$.value.tentativa_uuid,
              user_id: this._student$.value.usuario_id,
              rand_mode: this._student$.value.tentativa_modo_rand,
            },
          }),
        ),
      );
  }

  public onSave() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    this._alertService
      .confirm({
        title: 'geral.atencao',
        message: 'geral.confirmacao',
      })
      .pipe(take(1), notNull())
      .subscribe(() => this.save());
  }

  private save() {
    const data = {
      ...this.form.value,
      application_uuid: this._application.uuid,
      user_id: this._student$.value.usuario_id,
      attempt_uuid: this._student$.value.tentativa_uuid,
      rand_mode: this._student$.value.tentativa_modo_rand,
      question_uuid: this.question$.value.uuid,
    };
    this._store.dispatch(
      ModalAuditActions.QuestionLoadingReducer({ question_uuid: this.question$.value.uuid, loading: true }),
    );
    this._store.dispatch(ModalAuditActions.UpdateQuestionScoreEffect(data));
  }

  private initForm = ({ student, question }) => {
    this.form = this._formBuilder.group({
      feedback: [undefined],
      score: [undefined, [Validators.required, Validators.min(0), Validators.max(question.peso)]],
    });

    this.scoreConfig.max = question.peso;
  };

  private answerStatus(answer: AttemptQuestionData, alternative: AssessmentAlternative): AnswerResultEnum {
    const isSelected = answer?.alternativa?.uuid === alternative.uuid;
    const isCorrect = !!alternative.alternativa_correta || !!this.question$.value.sociodemografica;

    if (isSelected) {
      return isCorrect ? AnswerResultEnum.SelectedAndRight : AnswerResultEnum.SelectedAndWrong;
    }

    if (isCorrect) {
      return AnswerResultEnum.Right;
    }

    return AnswerResultEnum.None;
  }
}
